/**
 * A wheel anywhere on the window scrolls the screen it is over.
 *
 * The site chrome holds every screen to a measure (`--site-max`), so on a wide
 * window a third of the page is margin — and that margin is NOT inside the
 * screen's ScrollView. A wheel only ever chains UP to a scrollable ancestor,
 * never sideways into a sibling or down into a child, and everything above the
 * scroller here is either `overflow: hidden` (`.app-shell`) or not scrollable
 * at all. So a wheel over the margin, over the site header, or over the ground
 * beside a sheet did nothing whatsoever: the page scrolled or refused to,
 * depending on where the pointer happened to be sitting.
 *
 * A page owns its window. This hands the delta to the screen's own scroller
 * whenever nothing under the pointer can take it, and does the same for the
 * paging keys — which had the identical hole, since with nothing focused
 * PageDown goes to a document that does not scroll either.
 *
 * It never fights the browser: if anything under the pointer CAN scroll, this
 * stays out of the way, end of travel included.
 */

/** Overflow values that make a box its own scroll container. */
const SCROLLS = /^(auto|scroll|overlay)$/;

/** Does anything from `node` up to the document scroll vertically? */
function ownsScroll(node: Element | null): boolean {
  for (let el = node; el && el !== document.body; el = el.parentElement) {
    if (el.scrollHeight - el.clientHeight <= 1) continue;
    if (SCROLLS.test(getComputedStyle(el).overflowY)) return true;
  }
  return false;
}

/**
 * The scroller the screen is currently reading as "the page": the biggest one
 * that has somewhere to go. A modal covers the screen behind it, so while one
 * is open the search starts there instead.
 */
function screenScroller(): HTMLElement | null {
  const modals = document.querySelectorAll<HTMLElement>('.rn-modal');
  const scope: ParentNode = modals.length
    ? modals[modals.length - 1]
    : document.querySelector('.screen-root') ?? document;

  let best: HTMLElement | null = null;
  let bestArea = 0;
  for (const el of scope.querySelectorAll<HTMLElement>('.rn-scroll-v')) {
    if (el.scrollHeight - el.clientHeight <= 1) continue;
    const box = el.getBoundingClientRect();
    const area = box.width * box.height;
    if (area > bestArea) {
      bestArea = area;
      best = el;
    }
  }
  return best;
}

/** Wheel deltas come in pixels, lines or pages depending on the device. */
function pixels(e: WheelEvent, viewport: number): number {
  if (e.deltaMode === 1) return e.deltaY * 16;
  if (e.deltaMode === 2) return e.deltaY * viewport;
  return e.deltaY;
}

/** How far each paging key moves, given the scroller's height. */
const PAGING: Record<string, (h: number) => number | 'top' | 'bottom'> = {
  PageDown: (h) => h * 0.9,
  PageUp: (h) => -h * 0.9,
  Home: () => 'top',
  End: () => 'bottom',
};

/** Typing must never be read as paging. */
function isTextEntry(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
}

/** Starts forwarding; returns the teardown. */
export function installPageScroll(): () => void {
  const onWheel = (e: WheelEvent) => {
    // ctrl+wheel is the browser's zoom, not a scroll.
    if (e.defaultPrevented || e.ctrlKey || !e.deltaY) return;
    if (ownsScroll(e.target as Element)) return;
    const target = screenScroller();
    if (!target) return;
    target.scrollBy({ top: pixels(e, target.clientHeight) });
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
    const move = PAGING[e.key];
    if (!move) return;
    if (isTextEntry(e.target as Element) || ownsScroll(e.target as Element)) return;
    const target = screenScroller();
    if (!target) return;

    const to = move(target.clientHeight);
    e.preventDefault();
    if (to === 'top') target.scrollTo({ top: 0, behavior: 'smooth' });
    else if (to === 'bottom') target.scrollTo({ top: target.scrollHeight, behavior: 'smooth' });
    else target.scrollBy({ top: to, behavior: 'smooth' });
  };

  window.addEventListener('wheel', onWheel, { passive: true });
  window.addEventListener('keydown', onKeyDown);
  return () => {
    window.removeEventListener('wheel', onWheel);
    window.removeEventListener('keydown', onKeyDown);
  };
}
